(ns practitest.frontend.landing.assigned-to-me
  (:require [practitest.frontend.components2.container-card :refer [container-card]]
            [practitest.frontend.common.i18n :refer [get-translation format-translation]]
            [practitest.frontend.common.format-utils :refer [formatted-date-time]]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.overflow :as overflow]
            [practitest.frontend.components2.tabs :refer [tabs-container tab]]
            [practitest.frontend.common-entities.metadata :refer [get-entity-name]]
            [practitest.frontend.components2.run-status :as run-status]
            [practitest.frontend.subs :as subs]
            [re-frame.core :as re-frame]
            [reagent.core :as r]))

(defn entity-path [entity-type]
  (case entity-type
    "requirement" "requirements"
    "test"        "tests"
    "test-set"    "sets"
    "instance"    "instances"
    "issue"       "issues"))

(defn tab->permission [tab-id]
  (case tab-id
    :requirements "requirements_view"
    :tests        "tests_runs_view"
    :test-sets    "tests_runs_view"
    :instances    "tests_runs_view"
    :issues       "issues_view"
    nil))

(defn get-project-permissions
  "Returns the permissions set for a specific project, or nil if not found"
  [user-projects project-id]
  (some #(when (= (:id (:project %)) project-id)
           (:permissions %))
        user-projects))

(defn has-permission-for-project?
  "Checks if user has a specific permission in a specific project"
  [user-projects project-id permission]
  (contains? (get-project-permissions user-projects project-id) permission))

(defn tab->entity-name [tab-id]
  (case tab-id
    :requirements (get-entity-name :requirement :plural-name)
    :tests        (get-entity-name :test :plural-name)
    :test-sets    (get-entity-name :testset :plural-name)
    :instances    (get-translation "instance.linked" "Instances")
    :issues       (get-entity-name :issue :plural-name)
    "items"))

(defn empty-items-message [entity-name]
  [:div.pt-no-assigned-message
   (format-translation
    (get-translation "landing.assigned-to-me.empty-items"
                     "There are no %{entity-name} assigned to you")
    :entity-name entity-name)])

(defn assigned-item-title-link [{:keys [entity_type project_id id]}]
  (str "/p/" project_id "/"
       (entity-path entity_type)
       "/" id "/edit"))

(defn assigned-item-icon [entity-type]
  (case entity-type
    "requirement" [icons/mux-requirements]
    "test"        [icons/mux-test-library]
    "test-set"    [icons/mux-testsets-runs]
    "instance"    [icons/mux-test-library]
    "issue"       [icons/mux-issues]
    nil))

(defn run-status-badge [status]
  (when status
    (let [color (run-status/get-status-color status)
          label (run-status/get-status-label status)]
      [:span.pt-run-status-badge
       {:style {:background-color (str "color-mix(in srgb, " color " 30%, transparent)")
                :color color}}
       label])))

(defn assigned-item-card [{:keys [entity-type display-id title title-link updated-at icon project-name run-status]}]
  (let [entity-key (case entity-type
                     "requirement" :requirement
                     "test" :test
                     "test-set" :testset
                     "instance" nil
                     "issue" :issue
                     nil)
        entity-name (if entity-key
                      (get-entity-name entity-key :singular-name)
                      (get-translation "instance.linked" "Instance"))]
    [:div.similar-items-cards-container
      [:div.similar-item-card
       [:div.traceability-card-row.first-row
        [:span.entity-type-icon icon]
        [:span.entity-type-text
         (str entity-name " # " display-id " ")]
        [overflow/overflow-with-mux-tooltip
         {}
         (fn [props]
           [:span.name-with-link.ellipsis props
            [:a {:href title-link
                 :target "_blank"} title]])
         title]
        [run-status-badge run-status]
        [:span.timestamp-holder
         (formatted-date-time updated-at)]]
       [:div.entity-type-text
        "Project: " project-name]]]))

(defn highest-count-tab [permitted-counts]
  (let [max-tab (->> [:issues :instances :test-sets :tests :requirements]
                  (apply max-key #(get permitted-counts % 0)))]
    (if (pos? (get permitted-counts max-tab 0))
      max-tab
      :requirements)))

(defn assigned-to-me []
  (let [active-tab (r/atom :requirements)
        tab-initialized? (r/atom false)]
    (fn []
      (let [assigned-items-data @(re-frame/subscribe [:landing/assigned-items])
            assigned-items (or assigned-items-data
                               {:requirements []
                                :tests []
                                :test_sets []
                                :instances []
                                :issues []})
            user-projects @(re-frame/subscribe [::subs/user-projects])
            filter-permitted (fn [items tab-id]
                               (let [permission (tab->permission tab-id)]
                                 (filter #(has-permission-for-project? user-projects (:project_id %) permission) items)))
            permitted-counts {:requirements (count (filter-permitted (:requirements assigned-items) :requirements))
                              :tests        (count (filter-permitted (:tests assigned-items) :tests))
                              :test-sets    (count (filter-permitted (:test_sets assigned-items) :test-sets))
                              :instances    (count (filter-permitted (:instances assigned-items) :instances))
                              :issues       (count (filter-permitted (:issues assigned-items) :issues))}
            _ (when (and (not @tab-initialized?) assigned-items-data)
                (reset! tab-initialized? true)
                (reset! active-tab (highest-count-tab permitted-counts)))]
        [:div.pt-section
         [:h3.pt-container-card-title [icons/mux-pin] (get-translation "landing.assigned-to-me" "Assigned to Me")]
         [:p.pt-container-card-subtitle (get-translation "landing.assigned-to-me.subtitle" "Your 5 most recently updated items")]
         [container-card
          {:class "pt-assigned-to-me-card"}

          [tabs-container
           {:active-tab-id @active-tab
            :on-toggle #(reset! active-tab %)}

           [tab {:tab-id :requirements}
            (get-entity-name :requirement :plural-name)
            " (" (:requirements permitted-counts) ")"]

           [tab {:tab-id :tests}
            (get-entity-name :test :plural-name)
            " (" (:tests permitted-counts) ")"]

           [tab {:tab-id :test-sets}
            (get-entity-name :testset :plural-name)
            " (" (:test-sets permitted-counts) ")"]

           [tab {:tab-id :instances}
            (get-translation "instance.linked" "Instances")
            " (" (:instances permitted-counts) ")"]

           [tab {:tab-id :issues}
            (get-entity-name :issue :plural-name)
            " (" (:issues permitted-counts) ")"]]

          [:div.pt-assigned-items
           (let [items (case @active-tab
                         :requirements (:requirements assigned-items)
                         :tests (:tests assigned-items)
                         :test-sets (:test_sets assigned-items)
                         :instances (:instances assigned-items)
                         :issues (:issues assigned-items)
                         [])
                 permission (tab->permission @active-tab)
                 permitted-items (filter #(has-permission-for-project? user-projects (:project_id %) permission) items)]
             (if (seq permitted-items)
               (for [item permitted-items]
                 ^{:key (str (:entity_type item) "-" (:id item))}
                 [assigned-item-card
                  {:entity-type (:entity_type item)
                   :display-id (:display_id item)
                   :title (:title item)
                   :title-link (assigned-item-title-link item)
                   :updated-at (:updated_at item)
                   :icon (assigned-item-icon (:entity_type item))
                   :project-name (:project_name item)
                   :run-status (:run_status item)}])
               [empty-items-message (tab->entity-name @active-tab)]))]]]))))
